import React, { useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Tooltip, Tag } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateLiftSelected,
  updateBankSelected,
  updateModalControlPanelTab,
  updateOpenModalControlPanelLift,
  updateControlPanelDrawerPlacement,
  updateChannelId,
  updateServerId,
} from "../../../redux/monitoring/monitoringSlice";
// CUSTOM HOOKS
import {
  useLastLiftMessage,
  useDirectionForLift,
} from "../socketMessageState/BankMessagesContext";
// COMPONENTS
import IconDefinition from "../iconDefinition/IconDefinition";
// INTERFACES
import { LiftInterface } from "../../../interfaces/Lift.interface";
import { BankInterface } from "../../../interfaces/Bank.interface";
import { RootState } from "../../../app/store";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
// AUTHORIZATION
import validatePermission from "../../../utils/validatePermissions";
// UTILS
import { emptyFunctionDeclaration } from "../../../utils/utils";
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";
import {
  bitmaskToDoorIcon,
  bitmaskToDoorIconFiltered,
} from "../../../utils/bitmaskToIcon";
import { bitmaskPrefix } from "../../../utils/typedKeys";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
import { stringToUuidT } from "../../../utils/uuid";

function MonitoringLiftTopView({
  lift,
  bank,
  serverId,
  channelId,
}: {
  lift: LiftInterface;
  bank: BankInterface;
  serverId: string;
  channelId: string;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const theme = useSelector((state: RootState) => state.home.theme);
  const permissions = useSelector((state: RootState) => state.user.permissions);
  const doorUnknownAsOpening = useSelector(
    (state: RootState) => state.monitoring.doorUnknownAsOpening
  );
  const hideUnconfiguredDoors = useSelector(
    (state: RootState) => state.monitoring.hideUnconfiguredDoors
  );
  const previewFaultServiceMode = useSelector(
    (state: RootState) => state.monitoring.previewFaultServiceMode
  );

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(() => stringToUuidT(lift.id), [lift.id]);
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const hasFaults: boolean = previewFaultServiceMode || socketMessageLift.faults;

  const prefix = useMemo(
    () => bitmaskPrefix(socketMessageLift.bankId, socketMessageLift.liftId),
    [socketMessageLift.bankId, socketMessageLift.liftId]
  );
  const hasFrontDoor = useMemo(
    () => lift.lift_floors?.some((lf) => lf.front_door) ?? true,
    [lift.lift_floors]
  );
  const hasRearDoor = useMemo(
    () => lift.lift_floors?.some((lf) => lf.rear_door) ?? true,
    [lift.lift_floors]
  );
  const doorIcon = useMemo(
    () =>
      hideUnconfiguredDoors
        ? bitmaskToDoorIconFiltered(
          socketMessageLift.bitmask,
          prefix,
          hasFrontDoor,
          hasRearDoor,
          doorUnknownAsOpening
        )
        : bitmaskToDoorIcon(
          socketMessageLift.bitmask,
          prefix,
          doorUnknownAsOpening
        ),
    [
      socketMessageLift.bitmask,
      prefix,
      doorUnknownAsOpening,
      hideUnconfiguredDoors,
      hasFrontDoor,
      hasRearDoor,
    ]
  );
  const { directionIcon, lastDirectionIcon } = useDirectionForLift(liftUuid);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getTabKey = () => {
    const key = "CARCALLS";
    if (hasFaults) {
      return "FAULTS";
    }
    if (validatePermission("monitor-control-panel-car-calls", permissions)) {
      return "CARCALLS";
    }
    if (validatePermission("monitor-control-panel-hall-calls", permissions)) {
      return "HALLCALLS";
    }
    if (validatePermission("monitor-control-panel-actions", permissions)) {
      return "ACTIONS";
    }
    if (validatePermission("monitor-control-panel-faults", permissions)) {
      return "FAULTS";
    }
    if (validatePermission("monitor-control-panel-status", permissions)) {
      return "STATUS";
    }
    return key;
  };

  const getDrawerPlacement = (e: any) => {
    const clickX = e.clientX;
    const screenWidth = window.innerWidth;
    if (clickX > screenWidth / 2) {
      dispatch(updateControlPanelDrawerPlacement("left"));
    } else {
      dispatch(updateControlPanelDrawerPlacement("right"));
    }
  };

  const onClickOpenModalElevator = (e: any) => {
    getDrawerPlacement(e);
    dispatch(
      updateModalControlPanelTab({
        liftType: "ELEVATOR",
        tab: getTabKey(),
      })
    );
    dispatch(updateLiftSelected(lift));
    dispatch(updateBankSelected(bank));
    dispatch(updateChannelId(channelId));
    dispatch(updateServerId(serverId));
    dispatch(updateOpenModalControlPanelLift(true));
  };

  // ************************************************ */
  // COMPONENT ************************************** */
  const displayDirection = directionIcon || lastDirectionIcon;
  const directionDimmed = !directionIcon && !!lastDirectionIcon;

  return (
    <div>
      <div
        style={{
          height: "50px",
          width: "65px",
          display: "flex",
          flexDirection: "column",
          marginLeft: "5px",
          marginRight: "5px",
        }}
        className={`monitoringView__topViewContainer__${theme}  ${hasFaults ? `monitoringView__dangerElevator   ` : ""
          }${hasFaults ? `monitoringView__dangerElevator__top ` : ""}${hasFaults ? `monitoringView__dangerElevator__bottom` : ""
          }  `}
      >
        <div
          className={`monitoringView__topViewContainer__${theme}`}
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {doorIcon && (
            <IconDefinition
              isMap={false}
              expanded={false}
              liftType="ELEVATOR"
              icon={doorIcon}
              iconSize={20}
            />
          )}
          {displayDirection && (
            <span style={{ opacity: directionDimmed ? 0.3 : 1 }}>
              <IconDefinition
                isMap={false}
                expanded={false}
                liftType="ELEVATOR"
                icon={displayDirection}
                iconSize={20}
              />
            </span>
          )}
        </div>
      </div>
      {/** ELEVATOR NAME */}
      <div
        role="button"
        tabIndex={0}
        onKeyDown={emptyFunctionDeclaration}
        onClick={onClickOpenModalElevator}
        style={{
          fontSize: "9px",
          fontWeight: "bolder",
          textAlign: "center",
          marginTop: "5px",
        }}
      >
        <div>
          <Tooltip placement="top" title={lift.name}>
            <span>{lift.name.toUpperCase().substring(0, 6)}</span>
          </Tooltip>
        </div>
        {!hasFaults && !socketMessageLift.serviceMode && !previewFaultServiceMode && (
          <div style={{ height: "10px" }} />
        )}
        {hasFaults && (
          <div className="mt-1">
            <Tag
              style={{
                marginTop: "5px",
                fontSize: "10px",
                width: "90%",
                marginLeft: "5%",
                marginRight: "5%",
              }}
              color="red"
            >
              FAULT
            </Tag>
          </div>
        )}
        {(socketMessageLift.serviceMode || previewFaultServiceMode) &&
          (() => {
            if (previewFaultServiceMode && !socketMessageLift.serviceMode) {
              return (
                <div className="mt-1 mb-2">
                  <Tooltip placement="top" title="Energy Saving">
                    <Tag
                      style={{
                        marginTop: "5px",
                        fontSize: "10px",
                        width: "90%",
                        marginLeft: "5%",
                        marginRight: "5%",
                      }}
                      color="blue"
                    >
                      ENER
                    </Tag>
                  </Tooltip>
                </div>
              );
            }
            const display = getServiceModeDisplay(
              socketMessageLift.serviceMode.messageType,
              socketMessageLift.serviceMode.message?.message ?? null
            );
            if (!display) return null;
            return (
              <div className="mt-1 mb-2">
                <Tooltip placement="top" title={display.displayName}>
                  <Tag
                    style={{
                      marginTop: "5px",
                      fontSize: "10px",
                      width: "90%",
                      marginLeft: "5%",
                      marginRight: "5%",
                    }}
                    color={display.color}
                  >
                    {display.abbreviation.substring(0, 4)}
                  </Tag>
                </Tooltip>
              </div>
            );
          })()}
      </div>
    </div>
  );
}

export default React.memo(MonitoringLiftTopView);
